import {useSyncExternalStore} from 'react';
import {
    createUserMediaHooks,
    useSelectDeviceChangeHandler,
    getAudioOutput,
    useUserMediaContext,
} from '@pexip/media-components';

import {mediaSignals, muteAudio, muteVideo} from '../contexts/media.context';
import {useConfig} from '../contexts/config.context';
import {logger} from '../logger';
import {config} from '../config';

export const {
    useAudioInput,
    useAudioInputUnavailable,
    useAudioMuteState,
    useAudioSuspended,
    useDevices,
    useHaveRequestedAudio,
    useHaveRequestedMedia,
    useHaveRequestedVideo,
    useInputDeviceStatusInfo,
    useMediaStatus,
    useMediaStream,
    useSourceAudioTrack,
    useSourceVideoTrack,
    useToggleAudioMuted,
    useToggleVideoMuted,
    useVideoInput,
    useVideoInputUnavailable,
    useVideoMuteState,
    useVideoSuspended,
} = createUserMediaHooks({
    signals: mediaSignals,
    muteVideo,
    muteAudio,
});

export const useVideoInputChangeHandler = () => {
    const userMedia = useUserMediaContext();
    const devices = useDevices();
    const selectedVideoInput = useVideoInput();

    return useSelectDeviceChangeHandler({
        currentInput: selectedVideoInput,
        devices,
        requestMedia: device => {
            if (!device) {
                return;
            }
            const constraints = {video: {device: {exact: device}}};
            logger.debug(constraints, 'Save VideoInput');
            config.set({
                key: 'videoInput',
                value: device,
                persist: true,
            });

            userMedia.getUserMedia(constraints);
        },
    });
};

export const useAudioInputChangeHandler = () => {
    const userMedia = useUserMediaContext();
    const devices = useDevices();
    const selectedAudioInput = useAudioInput();

    return useSelectDeviceChangeHandler({
        currentInput: selectedAudioInput,
        devices,
        requestMedia: device => {
            if (!device) {
                return;
            }
            const constraints = {audio: {device: {exact: device}}};
            logger.debug(constraints, 'Save AudioInput');
            config.set({
                key: 'audioInput',
                value: device,
                persist: true,
            });

            userMedia.getUserMedia(constraints);
        },
    });
};

export const useAudioOutputChangeHandler = (persist: boolean) => {
    const [savedAudioOutput, setSavedAudioOutput] = useConfig('audioOutput');
    const devices = useDevices();

    const selectedAudioOutput = getAudioOutput(devices, savedAudioOutput);
    return useSelectDeviceChangeHandler({
        currentInput: selectedAudioOutput,
        devices,
        requestMedia: device => {
            if (!device) {
                return;
            }
            setSavedAudioOutput(device, persist);
        },
    });
};

export const useUpdatingAudio = (
    subscribe = mediaSignals.onUpdatingAudio.add,
) => {
    const userMedia = useUserMediaContext();
    return useSyncExternalStore(subscribe, () => userMedia.updatingAudio);
};
export const useUpdatingVideo = (
    subscribe = mediaSignals.onUpdatingVideo.add,
) => {
    const userMedia = useUserMediaContext();
    return useSyncExternalStore(subscribe, () => userMedia.updatingVideo);
};
